import { gql, useMutation } from "@apollo/client";
import { Mutation, MutationResetUserPasswordArgs } from "@generated/graphql";
import { LoadingButton } from "@mui/lab";
import { Button, Card, Stack, Typography } from "@mui/material";
import AuthPasswordFields from "components/auth/AuthPasswordFields";
import Form from "components/form/Form";
import ROUTES from "constants/routes";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { parseApolloError } from "helpers/errorsParser";
import { useContext } from "react";
import { useForm } from "react-hook-form";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";

const RESET_USER_PASSWORD_MUTATION = gql`
  mutation resetUserPassword($password: String!, $token: String!) {
    resetUserPassword(password: $password, token: $token)
  }
`;

type ResetPasswordFormValues = {
  password: string;
  confirmPassword: string;
};

const FIELD_LABEL_PROPS = {
  variant: "body16rg" as const,
  sx: {
    color: "base.500",
    fontWeight: 600,
    mb: 0.5,
  },
};

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const { addToast } = useContext(ToastContext);
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const form = useForm<ResetPasswordFormValues>({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });
  const { handleSubmit, watch } = form;
  const password = watch("password");

  const [resetUserPassword, { loading }] = useMutation<
    Pick<Mutation, "resetUserPassword">,
    MutationResetUserPasswordArgs
  >(RESET_USER_PASSWORD_MUTATION);

  const onSubmit = async (formData: ResetPasswordFormValues) => {
    if (!token) {
      addToast({ type: ToastTypeEnum.ERROR, text: "В ссылке отсутствует токен для сброса пароля" });
      return;
    }

    try {
      const result = await resetUserPassword({
        variables: {
          password: formData.password,
          token,
        },
      });

      if (!result.data?.resetUserPassword) {
        addToast({ type: ToastTypeEnum.ERROR, text: "Не удалось обновить пароль" });
        return;
      }

      addToast({ type: ToastTypeEnum.SUCCESS, text: "Пароль успешно обновлен" });
      navigate(`/${ROUTES.LOGIN}`);
    } catch (error) {
      parseApolloError(error, {
        addToast,
        defaultText: "Не удалось обновить пароль",
      });
    }
  };

  if (!token) {
    return (
      <Card
        variant='outlined'
        sx={{ borderRadius: 3, p: { xs: 3, sm: 4 } }}>
        <Stack
          spacing={3}
          alignItems='flex-start'>
          <Stack spacing={1}>
            <Typography variant='h28'>Сброс пароля недоступен</Typography>
            <Typography
              variant='body16rg'
              sx={{ color: "text.secondary", maxWidth: 440 }}>
              В ссылке отсутствует токен. Проверьте письмо и перейдите по ссылке из него еще раз.
            </Typography>
          </Stack>

          <Button
            component={RouterLink}
            to={`/${ROUTES.LOGIN}`}
            variant='contained'
            size='large'
            sx={{ p: 0 }}>
            На страницу входа
          </Button>
        </Stack>
      </Card>
    );
  }

  return (
    <Stack spacing={3.5}>
      <Stack spacing={1}>
        <Typography variant='h28'>Создайте новый пароль</Typography>
        <Typography
          variant='body16rg'
          sx={{ color: "text.secondary", maxWidth: 460 }}>
          Укажите новый пароль для входа в систему. После сохранения перенаправим вас на страницу авторизации.
        </Typography>
      </Stack>

      <Form
        form={form}
        onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2.5}>
          <AuthPasswordFields
            password={password}
            passwordLabel='Новый пароль'
            passwordPlaceholder='Минимум 8 символов'
            labelTypographyProps={FIELD_LABEL_PROPS}
          />

          <LoadingButton
            type='submit'
            loading={loading}
            variant='contained'
            size='large'>
            Сохранить пароль
          </LoadingButton>

          <Button
            component={RouterLink}
            to={`/${ROUTES.LOGIN}`}
            variant='text'
            size='large'>
            На страницу входа
          </Button>
        </Stack>
      </Form>
    </Stack>
  );
};

export default ResetPasswordPage;
